import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { LFC } from '../../Legacy';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { EntityWithLabel } from '../../api/ApiTypes';
import { Button, usePrompt } from '../../ui';
import { ResultInfoForMutation } from '../../ui/ResultInfo';
import { AppEvents } from '../AppEvents';
import { useResolve } from '../Container';
import { Copy, Em, Flexer, Spacer } from '../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../popups/Popup';

const useBoardDeleteMutation = () => {
    const api = useResolve(ApiClient);
    const evts = useResolve(AppEvents);

    return useMutation({
        mutationFn: async (params: { boardId: string }) => {
            await api.exec(v2.boards.delete(params));
        },
        onSuccess(_, { boardId }) {
            evts.push('board:delete', { boardId });
        }
    });
};

export const newBoardDeletePopup = (board: EntityWithLabel) => ({ ui: NewBoardDeletePopup, args: { board } });

export const NewBoardDeletePopup: LFC<PopupProps> = ({ action, dismiss, args }) => {
    const { board } = args;

    const mutation = useBoardDeleteMutation();

    const onAction = (confirm: boolean) => {
        if (confirm) {
            if (!mutation.isLoading) {
                mutation
                    .mutateAsync({
                        boardId: board.id
                    })
                    .then(() => action(true));
            }
        }
        else {
            dismiss();
        }
    };

    const promptRef = usePrompt({
        onAction: mutation.isLoading ? null : onAction
    });

    return <Flexer ref={promptRef}>
        <Popup w={32}>
            <Popup.Close onAction={() => onAction(false)} />
            <Popup.Title>Delete Board</Popup.Title>
            <Popup.Content>
                <Copy>
                    Are you sure you want to <Em>permanently delete</Em> this board? This cannot be undone.
                </Copy>
                <Spacer f={2} />
                <ResultInfoForMutation mutation={mutation} />
            </Popup.Content>
            <Popup.Actions>
                <Button disabled={mutation.isLoading} variant="negative" onAction={() => onAction(true)}>
                    {mutation.isLoading ? 'Deleting...' : 'Delete'}
                </Button>
                <Button disabled={mutation.isLoading} onAction={() => onAction(false)}>
                    Cancel
                </Button>
            </Popup.Actions>
        </Popup>
    </Flexer>;
};
